import appStore from '@stores/appStore'

/**
 * Which upload button the user pressed. Sent to the server as `purviewMode`.
 *
 * Mirrors PurviewUploadMode on the backend.
 */
export enum PurviewUploadMode {
  /** Decrypt if protected and this tenant is authorised. Used by integrations, not by this UI. */
  Auto = 'Auto',
  /** "Upload file" — store as-is. A labelled file is refused rather than decrypted. */
  Standard = 'Standard',
  /** "Upload labelled file" — remove the Microsoft Purview protection first. */
  Labeled = 'Labeled',
}

/**
 * Whether this tenant can accept Purview-labelled uploads.
 *
 * Read from the tenant settings the app already loads at boot (`app/tenants/myTenantSetting` →
 * `appStore.currentTenant.settings`), so reading it costs no extra request. The backend computes it
 * rather than exposing the raw configuration: it is true only when the feature is on AND at least
 * one directory is usable, because a button that can only ever fail is worse than no button — a user
 * would press it, be refused, and reasonably read that as a bug.
 *
 * The directory list itself never reaches the browser; it holds a client secret per directory.
 */
export const isPurviewLabeledUploadEnabled = (): boolean =>
  (
    appStore.currentTenant?.settings?.['ICS.PurviewProtection.Enabled'] ?? ''
  ).toLowerCase() === 'true'

/**
 * Kill switch, in code, for the whole two-button upload chooser.
 *
 * Set to false and every upload control in the product goes back to exactly what it was before the
 * chooser existed: the plain drag-and-drop area, no hover overlay, no buttons, and `Auto` on the
 * server (a labelled file is decrypted if possible rather than refused). Nothing else needs
 * touching, no deploy of the backend, no tenant configuration change.
 *
 * It exists because the chooser sits on the upload path of every screen in the product. If it turns
 * out to get in the way, one boolean is a far better answer than reverting a feature that the
 * backend and the settings screen also depend on.
 *
 * Same shape as `AttachmentAccessChecker.RedactionEnforcementEnabled` on the backend.
 */
export const PURVIEW_UPLOAD_CHOOSER_ENABLED = true

/**
 * THE decision for whether to show the two-button chooser. One place, deliberately.
 *
 * Every caller must go through this rather than reading the tenant setting directly, so the rule
 * stays in one place as it grows.
 *
 * When it returns false the upload control renders exactly as it did before, and uploads keep the
 * previous server-side behaviour (`Auto`). That pairing matters: without the buttons there is no
 * other way for a user to upload a labelled document, so refusing one would leave them stuck with
 * nowhere to go.
 */
export const isPurviewUploadChooserEnabled = (): boolean => {
  if (!PURVIEW_UPLOAD_CHOOSER_ENABLED) {
    return false
  }

  // Anonymous uploads (the LEAP iframe) have no chooser: there is no signed-in user to ask, and the
  // server keeps them on Auto regardless.
  if (!appStore.currentUser?.id) {
    return false
  }

  // ---- Role-based gating goes here. For example:
  //        if (appStore.ifOffshoreStaff()) return false
  //        if (!appStore.currentUser?.roles?.includes(ROLE_NAME.ADMIN)) return false
  //      Returning false is always safe: the user simply gets the previous upload experience.

  return isPurviewLabeledUploadEnabled()
}
